import React from 'react'
import styled from 'styled-components'
import Modal, {
  CloseButton,
  PrevButton
} from '@jarvis.oddle/core/components/Modal'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import Form from '@jarvis.oddle/core/components/Form'
import { toDeliveryAddress } from '@jarvis.oddle/core/tools/address'
import { PortalDest } from '@jarvis.oddle/core/components/Portal'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import AddressSearch from 'src/components/molecules/AddressSearch'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import GoogleMapService from 'src/containers/MapService/GoogleMapService'
import { isObjectEmpty } from 'src/utils/object'
import { isEmpty } from 'src/utils/string'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import {
  syncDeliveryAddressTwoAction,
  syncDeliveryLocationAction
} from 'src/redux/order/action'
import { setDefaultUserDeliveryAddressAction, selectUser } from 'src/redux/user'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import { makeControlledModal } from 'src/decorators/ModalController'

const StyledModalSection = styled(Modal.Section)`
  && {
    display: flex;
    flex-grow: 1;
    flex-shrink: 0;
    padding-bottom: 0;
  }
`
const DeliverySelectAddressModal = ({
  modal,
  mapApi
}: {
  modal: {
    show: boolean
    onClose: () => void
    onClosed: () => void
  }
  mapApi: {
    reverseGeoCode: (query) => Promise<Record<string, unknown>>
  }
}) => {
  const { t } = React.useContext(TranslationContext)
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectUser)
  const meta = useAppSelector(selectDetailMenuSite)
  const convertAddressSearchInput = React.useCallback(
    (address, addressTwo = '') => {
      return {
        deliveryAddress: address.formattedAddress,
        deliveryAddressOne: address.addressOne,
        deliveryAddressTwo: addressTwo,
        deliveryAddressState: address.state,
        deliveryAddressCity: address.city,
        deliveryAddressPostal: address.postal,
        deliveryAddressCountry: meta?.menuCountry,
        latitude: address.latitude,
        longitude: address.longitude,
        addressTwoDetails: address.addressTwoDetails
      }
    },
    [meta?.menuCountry]
  )

  const handleSubmit = React.useCallback(
    async (
      { selectedSavedAddress, deliveryAddress, addressTwo },
      { setFieldError, setSubmitting }
    ) => {
      const selectedAddress = selectedSavedAddress
        ? (user.data.deliveryAddresses || []).find(
            ({ id }) => id === selectedSavedAddress
          )
        : null
      let address
      if (selectedAddress) {
        if (isEmpty(selectedAddress.autoCompleteAddress)) {
          const data = await mapApi.reverseGeoCode({
            latitude: selectedAddress.latitude,
            longitude: selectedAddress.longitude
          })
          if (data.length > 0) {
            address = convertAddressSearchInput(data[0])
          } else {
            address = convertAddressSearchInput({
              ...selectedAddress,
              addressOne: selectedAddress.formattedAddress
            })
          }
        } else {
          address = toDeliveryAddress(selectedAddress)
        }
      } else {
        address = convertAddressSearchInput(deliveryAddress, addressTwo)
      }

      const syncResponse = !isObjectEmpty(selectedAddress)
        ? await dispatch(syncDeliveryLocationAction({ address }))
        : isEmpty(addressTwo)
        ? { meta: { requestStatus: 'fulfilled' }, payload: { error: null } }
        : await dispatch(syncDeliveryAddressTwoAction({ addressTwo }))

      if (
        syncResponse.meta.requestStatus === 'fulfilled' &&
        isEmpty(syncResponse.payload?.error) &&
        isEmpty(syncResponse.payload?.payload?.error)
      ) {
        if (selectedSavedAddress) {
          dispatch(setDefaultUserDeliveryAddressAction(selectedSavedAddress))
        }
        // FESC-1588 Remove call to ajax-create-address during delivery address input
        // else {
        //   createThenSetUserDefaultAddressAction(address)
        // }
        pickUpDeliveryFlow.send('dateTimeSelection')
      } else {
        const error = t(
          syncResponse.payload?.error || syncResponse.payload?.payload?.error
        )
        if (selectedAddress) {
          setFieldError('selectedSavedAddress', error)
        } else {
          setFieldError('deliveryAddress', error)
        }
      }
      setSubmitting(false)
    },
    [
      convertAddressSearchInput,
      dispatch,
      mapApi,
      t,
      user.data.deliveryAddresses
    ]
  )

  const validateAndSetStoreOrderAddress = React.useCallback(
    async (address) => {
      const deliveryAddress = convertAddressSearchInput(address)
      const result = await dispatch(
        syncDeliveryLocationAction({
          address: deliveryAddress
        })
      )
      return {
        valid:
          result.meta.requestStatus === 'fulfilled' &&
          isEmpty(result.payload?.error) &&
          isEmpty(result.payload?.payload?.error),
        message:
          result.meta.requestStatus === 'fulfilled' &&
          isEmpty(result.payload?.error) &&
          isEmpty(result.payload?.payload?.error)
            ? ''
            : t('Sorry, we are unable to deliver to this address')
      }
    },
    [convertAddressSearchInput, dispatch, t]
  )

  const selectedSavedAddress =
    user.data.preferredDeliveryAddress && user.data.preferredDeliveryAddress.id
  const countryCode = meta?.countryCode
  const hideAddressTwo =
    meta?.features?.enableNewCheckoutFlow &&
    meta?.checkoutAddressFormat &&
    (meta?.checkoutAddressFormat?.addressTwoFormat || []).every(
      (data) => data.displayInCart === false
    )

  return (
    <Modal {...modal} slideInAnimation='bottom'>
      <Form
        initialValues={{ selectedSavedAddress }}
        onSubmit={handleSubmit}
        disableAutocomplete
      >
        <Modal.FlexContainer>
          <Modal.Header>
            <PrevButton onClick={() => pickUpDeliveryFlow.prev()} />
            <H4 margin='none' bold>
              {t('Delivery')}
            </H4>
            <CloseButton onClick={modal.onClose} />
          </Modal.Header>
          <Modal.Body>
            <StyledModalSection>
              <AddressSearch
                submitButtonText={t('Select date & time')}
                savedAddresses={user.data.deliveryAddresses || []}
                countryCode={countryCode}
                validator={validateAndSetStoreOrderAddress}
                hideAddressTwo={hideAddressTwo}
              />
            </StyledModalSection>
          </Modal.Body>
          <Modal.Footer>
            <PortalDest channel='AddressSearchSubmitButton' />
          </Modal.Footer>
        </Modal.FlexContainer>
      </Form>
    </Modal>
  )
}

const WrappedDeliverySelectAddressModal = (props) => {
  return (
    <GoogleMapService>
      {(mapServiceProps) => (
        <DeliverySelectAddressModal {...props} {...mapServiceProps} />
      )}
    </GoogleMapService>
  )
}

export default makeControlledModal(
  WrappedDeliverySelectAddressModal,
  'DeliverySelectAddressModal'
)
